/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { Action } from '@reduxjs/toolkit';
import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type {
  ScopeName,
  PluginManager,
  ScopeStateMap,
  ScopeEventMap,
} from '@opengeoweb/plugin-interface';

/**
 * Hook to send events for a scope
 */
export function useScopeEvent<T extends ScopeName>(
  pluginBridge: PluginManager,
  scope: T,
): (event: ScopeEventMap[T]) => void {
  const sendEvent = useCallback(
    (event: ScopeEventMap[T]) => {
      pluginBridge.sendEvent(scope, event);
    },
    [pluginBridge, scope],
  );

  return sendEvent;
}

// Returns true if event matches the bridge’s current state (no-op), so useSendOnReduxChange skips it and avoids plugin → Redux → plugin echoes.
export type IsEventRoundTrip<T extends ScopeName> = (
  event: ScopeEventMap[T],
  bridgeState: ScopeStateMap[T] | undefined,
) => boolean;

export function useSendOnReduxChange<T extends ScopeName, R>(
  pluginBridge: PluginManager,
  scope: T,
  reduxState: R,
  toEvent: (state: R) => ScopeEventMap[T] | ScopeEventMap[T][] | null,
  enabled = true,
  isEventRoundTrip?: IsEventRoundTrip<T>,
): void {
  const sendEvent = useScopeEvent(pluginBridge, scope);
  const lastSentValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const eventOrEvents = toEvent(reduxState);
    if (!eventOrEvents) {
      return;
    }
    const events = Array.isArray(eventOrEvents)
      ? eventOrEvents
      : [eventOrEvents];
    if (events.length === 0) {
      return;
    }

    const bridgeState = pluginBridge.getState(scope);
    const filteredEvents = events.filter(
      (event) => !isEventRoundTrip?.(event, bridgeState),
    );
    if (filteredEvents.length === 0) {
      return;
    }

    // Skip if we'd send the same value
    const eventString = JSON.stringify(filteredEvents);
    if (eventString === lastSentValueRef.current) {
      return;
    }

    lastSentValueRef.current = eventString;
    filteredEvents.forEach(sendEvent);
  }, [
    reduxState,
    enabled,
    sendEvent,
    toEvent,
    pluginBridge,
    scope,
    isEventRoundTrip,
  ]);
}

/**
 * Push a source value into a read-only plugin scope via `setScopeState`.
 *
 * Use for scopes whose `ScopeEventMap` entry is `never` (e.g. `applicationConfiguration`)
 * — plugins subscribe but cannot dispatch events, so the host has to replace the
 * scope state directly.
 */
export function useSetScopeStateOnChange<T extends ScopeName, R>(
  pluginBridge: PluginManager,
  scope: T,
  source: R,
  toState: (state: R) => ScopeStateMap[T] | null,
  enabled = true,
): void {
  const lastSentStateRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const state = toState(source);
    if (state === null) {
      return;
    }
    const stateString = JSON.stringify(state);
    if (stateString === lastSentStateRef.current) {
      return;
    }
    lastSentStateRef.current = stateString;
    pluginBridge.setScopeState(scope, state);
  }, [source, enabled, toState, pluginBridge, scope]);
}

/**
 * Subscribe to plugin-originated events for a scope and dispatch the
 * resulting Redux actions.
 */
export function useDispatchOnScopeEvent<T extends ScopeName>(
  pluginBridge: PluginManager,
  scope: T,
  handler: (event: ScopeEventMap[T]) => Action | Action[] | null,
  enabled = true,
): void {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    return pluginBridge.subscribeToEvent(scope, (event) => {
      const actions = handler(event);
      if (!actions) {
        return;
      }
      const actionArray = Array.isArray(actions) ? actions : [actions];
      actionArray.forEach(dispatch);
    });
  }, [pluginBridge, scope, handler, enabled, dispatch]);
}
